import {Dexie} from 'dexie';
import {
    IVanillaReaderHighlightData,
    VanillaHighlightDataCallback,
    VanillaVoidCallback
} from '../VanillaReader/VanillaReaderModel';
import {IVanillaReaderHighlightDataStore} from './IVanillaReaderHighlightDataStore';
import {IVanillaReaderHighlightStoreData} from "./VanillaReaderStoreModel";

/**
 * # VanillaReaderHighlightIndexedDbDataStore
 *
 *  ## RESPONSIBILITIES
 *
 * This class acts as a runtime storage for the highlights that have been added to a publication.
 * Again, it does not persist its state anywhere. To save highlights between sessions call `getHighlights()` and
 * store the returned data. To restore state you provide the same data to the `addHighlights()` method.
 *
 * ## NOTE
 *
 * This is just the runtime storage, the rendering of highlights is done in the `VanillaReaderAnnotationLayerHighlights`
 * class.
 *
 */

export class VanillaReaderHighlightIndexedDbDataStore extends Dexie implements IVanillaReaderHighlightDataStore {
    private _highlights: Dexie.Table<IVanillaReaderHighlightStoreData, string>;

    private _highlightAddedCallback: VanillaHighlightDataCallback | undefined = undefined;
    private _highlightDeletedCallback: VanillaHighlightDataCallback | undefined = undefined;
    private _highlightUpdatedCallback: VanillaHighlightDataCallback | undefined = undefined;
    private _highlightsCollectionUpdatedCallback: VanillaVoidCallback | undefined = undefined;

    private _initialized: boolean = false;

    constructor(dbName: string = 'highlights.vanillareader.colibrio.com') {
        super(dbName);

        this.version(1).stores({
            highlights: '_id, locator, color, bookSection, publicationId, dateCreated',
        });

        this._highlights = this.table('highlights');

    }

    initialize(): Promise<void> {
        return this.open().then(_db => {
            this._initialized = true
        }).catch(_ => {
            this._initialized = false
        });
    }

    onHighlightAdded(callback: VanillaHighlightDataCallback): void {
        this._highlightAddedCallback = callback;
    }

    onHighlightUpdated(callback: VanillaHighlightDataCallback): void {
        this._highlightUpdatedCallback = callback;
    }

    onHighlightDeleted(callback: VanillaHighlightDataCallback): void {
        this._highlightDeletedCallback = callback;
    }

    onHighlightsCollectionUpdated(callback: VanillaVoidCallback): void {
        this._highlightsCollectionUpdatedCallback = callback;
    }

    async addHighlight(highlightData: IVanillaReaderHighlightData): Promise<IVanillaReaderHighlightData> {
        if (this._initialized) {
            let targetLocator = highlightData.locator;
            let targetHasHighlight = await this._highlights.where('locator').equals(targetLocator).count() > 0;
            if (!targetHasHighlight) {
                return this._addHighlight(highlightData).catch();
            } else {
                console.warn(`VanillaReaderHighlightStore.addHighlight(): location "${targetLocator}" is already highlighted.`);
                return Promise.reject(`VanillaReaderHighlightStore.addHighlight(): location "${targetLocator}" is already highlighted.`);
            }
        } else {
            return Promise.reject("No datastore connection")
        }
    }

    async addHighlights(highlightData: IVanillaReaderHighlightData[], silent: boolean = true): Promise<void> {
        highlightData.forEach((highlight: IVanillaReaderHighlightData) => {
            this._addHighlight(highlight, silent).catch(console.warn);
        });
        return Promise.resolve();
    }

    async updateHighlight(highlightData: IVanillaReaderHighlightData): Promise<void> {
        if (this._initialized) {
            if (highlightData._id && await this._highlights.get(highlightData._id)) {

                highlightData.dateUpdated = Date.now();

                this._highlights.update(highlightData._id, highlightData);

                const highlight = VanillaReaderHighlightIndexedDbDataStore.toApplicationData(highlightData);
                if (this._highlightUpdatedCallback) {
                    this._highlightUpdatedCallback(highlight);
                }
                if (this._highlightsCollectionUpdatedCallback) {
                    this._highlightsCollectionUpdatedCallback();
                }

            } else {
                console.warn(`VanillaReaderHighlightStore.addHighlight(): no highlight found for location "${highlightData.locator}".`);
                return Promise.reject(`VanillaReaderHighlightStore.addHighlight(): no highlight found for location "${highlightData.locator}".`);
            }
        } else {
            return Promise.reject("No datastore connection")
        }

    };

    async deleteHighlight(locator: string): Promise<void> {
        if (this._initialized) {
            let highlight = await this._highlights.where('locator').equals(locator).first();

            if (highlight && highlight._id) {
                this._highlights.delete(highlight._id);
                const applicationData = VanillaReaderHighlightIndexedDbDataStore.toApplicationData(highlight);
                if (this._highlightDeletedCallback) {
                    this._highlightDeletedCallback(applicationData);
                }

                if (this._highlightsCollectionUpdatedCallback) {
                    this._highlightsCollectionUpdatedCallback();
                }

            } else {
                console.warn(`VanillaReaderHighlightStore.deleteHighlight(): location "${locator}" is not highlighted.`);
                return Promise.reject(`VanillaReaderHighlightStore.deleteHighlight(): location "${locator}" is not highlighted.`);
            }
        } else {
            return Promise.reject(`VanillaReaderHighlightStore.deleteHighlight(): No database connection`);
        }
    }

    async deleteAllHighlights(): Promise<void> {
        if (this._initialized) {
            await this._highlights.each(async (highlight: IVanillaReaderHighlightStoreData) => {
                if (highlight._id) {
                    return this.deleteHighlight(highlight._id);
                } else {
                    return Promise.reject();
                }
            });
        }
        return Promise.resolve();
    }

    async deleteAllHighlightsByPublication(publicationId: string): Promise<void> {
        if (this._initialized) {
            await this._highlights.where('publicationId').equals(publicationId).delete();
            return Promise.resolve();
        } else {
            return Promise.resolve();
        }
    }

    async fetchHighlight(locator: string): Promise<IVanillaReaderHighlightData | undefined> {
        if (this._initialized) {
            let highlight = await this._highlights.where('locator').equals(locator).first()
                .then(VanillaReaderHighlightIndexedDbDataStore.maybeToApplicationData);
            if (highlight) {
                return highlight;
            } else {
                console.warn(`VanillaReaderHighlightStore.addHighlight(): no highlight found for location "${locator}".`);
            }
        }
        return;
    }

    async fetchAllHighlights(): Promise<IVanillaReaderHighlightData[]> {
        if (this._initialized) {
            return this._highlights.toArray()
                .then(storeData => storeData.map(VanillaReaderHighlightIndexedDbDataStore.toApplicationData));
        } else {
            return [];
        }
    }

    async fetchHighlightsByPublication(publicationId: string): Promise<IVanillaReaderHighlightData[]> {
        if (this._initialized) {
            return this._highlights.where('publicationId').equals(publicationId).toArray()
                .then(storeData => storeData.map(VanillaReaderHighlightIndexedDbDataStore.toApplicationData));
        } else {
            return [];
        }
    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    private async _addHighlight(
        highlightData: IVanillaReaderHighlightData,
        silent: boolean = false,
    ): Promise<IVanillaReaderHighlightData> {
        if (this._initialized) {
            let targetLocator = highlightData.locator;

            if (!highlightData.dateCreated) {
                highlightData.dateCreated = Date.now();
            }

            if (!highlightData._id) {
                highlightData._id = Date.now().toString();
            }

            const storeData = VanillaReaderHighlightIndexedDbDataStore.toStoreData(highlightData);
            await this._highlights.put(storeData, targetLocator);

            if (this._highlightAddedCallback) {
                this._highlightAddedCallback(highlightData, silent);
            }
            if (this._highlightsCollectionUpdatedCallback) {
                this._highlightsCollectionUpdatedCallback();
            }
        }
        return highlightData;
    }

    private static toStoreData(highlightData: IVanillaReaderHighlightData): IVanillaReaderHighlightStoreData {
        return {
            _id: highlightData._id,
            color: highlightData.color,
            comment: highlightData.comment,
            locator: highlightData.locator,
            selectionText: highlightData.selectionText,
            publicationId: highlightData.publicationId,
            bookSection: highlightData.bookSection,
            dateCreated: highlightData.dateCreated,
            dateUpdated: highlightData.dateUpdated
        }
    }

    private static maybeToApplicationData(storeData: IVanillaReaderHighlightStoreData | undefined): IVanillaReaderHighlightData | undefined {
        return storeData ? VanillaReaderHighlightIndexedDbDataStore.toApplicationData(storeData) : undefined;
    }

    private static toApplicationData(storeData: IVanillaReaderHighlightStoreData): IVanillaReaderHighlightData {
        return {
            _id: storeData._id,
            color: storeData.color,
            comment: storeData.comment,
            locator: storeData.locator,
            selectionText: storeData.selectionText,
            publicationId: storeData.publicationId,
            bookSection: storeData.bookSection,
            dateCreated: storeData.dateCreated,
        }
    }
}
